import type {
  SearchResultsSearchBy,
  SearchResultsSortBy,
} from "@/features/config/types";
import type { FlightWithPOI } from "@/features/flight_information/types";
import { type Floor, Poi, type PoiCategories } from "@/libs/models";
import type { Geofence } from "@/libs/models/geofence";

// 1. fuzzyScore
// 2. matchingWordsScore
// 3. allMatchLengthsScore
// 4. firstMatchLocationScore
const POI_NAME_WEIGHTS: [number, number, number, number] = [0.6, 0.2, 0.1, 0.1];
const POI_INFO_WEIGHTS: [number, number, number, number] = [0.4, 0.2, 0.4, 0];

export interface FilterOptions {
  // Adds pois given by ids to final search results
  includePoiIds?: Array<number>;
  // Removes pois given by ids from final search results
  excludePoiIds?: Array<number>;
  // Applies filters defined for search based on custom fields
  applySearchCfFilters?: boolean;
  // Applies filters defined for map visualization based on custom fields
  applyMapCfFilters?: boolean;
  // Ignores the selected categories when there is text search
  ignoreCategoryFiltersWhenSearching?: boolean;
  // Re-order search when distance changes
  triggerOnDistanceChange?: boolean;
  // Controls whether search results are ordered
  orderResults?: boolean;
  // Controls when the search resets its state
  resetDependencies?: any[];
}

export type SearchResultMatches = {
  indices: [number, number][];
  key: string;
}[];

export enum SearchResultKeys {
  NAME = "translatedName",
  INFO = "translatedInfo",
}

export enum SearchResultType {
  POI = "poi",
  GEOFENCE = "geofence",
  FLIGHT = "flight",
}

export enum WORKER_EVENTS {
  INIT = "worker.search.init",
  POPULATE = "worker.search.populate",
  SEARCH = "worker.search.compute",
  ORDER = "worker.search.order",
}

export type SearchableItemFieldsConfig<T = Poi | Geofence | FlightWithPOI> = {
  weights?: [number, number, number, number];
  /**
   * Multiplier applied to the weighted score when combining results across
   * multiple fields for the same item. Defaults to 1.0. Use values < 1 to
   * make a field contribute less when competing against higher-priority fields
   * (e.g. 0.8 for an "info" field so name matches are preferred over info matches).
   */
  combineWeight?: number;
  matchMode?: "fuzzy" | "exact";
  fieldKey: string;
  getter?: (item: T, lang?: string) => string | null;
};

export class SearchableItem<T = Poi | Geofence | FlightWithPOI> {
  private constructor(
    public item: T,
    public type: SearchResultType,
    public fieldsConfig: SearchableItemFieldsConfig<T>[] = [],
  ) {}

  static fromPoi(poi: Poi): SearchableItem<Poi> {
    return new SearchableItem<Poi>(poi, SearchResultType.POI, [
      {
        combineWeight: 0.8,
        fieldKey: SearchResultKeys.INFO,
        getter: (item: Poi, lang?: string) =>
          item.getTranslatedProcessedInfo(lang),
        matchMode: "fuzzy",
        weights: POI_INFO_WEIGHTS,
      },
      {
        fieldKey: SearchResultKeys.NAME,
        getter: (item: Poi, lang?: string) =>
          item.getTranslatedProperty("name", lang),
        matchMode: "fuzzy",
        weights: POI_NAME_WEIGHTS,
      },
    ]);
  }

  static fromGeofence(geofence: Geofence): SearchableItem<Geofence> {
    return new SearchableItem<Geofence>(
      geofence,
      SearchResultType.GEOFENCE,
      [],
    );
  }

  static fromFlightWithPOI(
    flightWithPOI: FlightWithPOI,
  ): SearchableItem<FlightWithPOI> {
    return new SearchableItem<FlightWithPOI>(
      flightWithPOI,
      SearchResultType.FLIGHT,
      [
        {
          fieldKey: "id",
          getter: (item: FlightWithPOI) => item.flight.id,
          matchMode: "exact",
          weights: [0.8, 0.1, 0.1, 0],
        },
        {
          fieldKey: "flightSearchText",
          getter: (item: FlightWithPOI) =>
            (item.flight.flightType === "ARRIVAL"
              ? [
                  item.flight.airlineName,
                  item.flight.airlineCode,
                  item.flight.from.airportName,
                  item.flight.from.airportCode,
                  item.flight.to.terminalCode,
                ]
              : [
                  item.flight.airlineName,
                  item.flight.airlineCode,
                  item.flight.to.airportName,
                  item.flight.to.airportCode,
                  item.flight.from.terminalCode,
                ]
            )
              .filter(Boolean)
              .join(" "),
          matchMode: "fuzzy",
          weights: [0.5, 0.3, 0.2, 0],
        },
      ],
    );
  }

  toJSON() {
    return {
      fieldsConfig: this.fieldsConfig,
      item: this.item,
      type: this.type,
    };
  }

  static fromMap(data: any): SearchableItem {
    return new SearchableItem(data.item, data.type, data.fieldsConfig);
  }

  isPoi(): this is SearchableItem & { type: SearchResultType.POI; item: Poi } {
    return this.type === SearchResultType.POI;
  }

  isGeofence(): this is SearchableItem & {
    type: SearchResultType.GEOFENCE;
    item: Geofence;
  } {
    return this.type === SearchResultType.GEOFENCE;
  }

  isFlight(): this is SearchableItem & {
    type: SearchResultType.FLIGHT;
    item: FlightWithPOI;
  } {
    return this.type === SearchResultType.FLIGHT;
  }
}

export class SearchResultItem<T = Poi | Geofence | FlightWithPOI> {
  constructor(
    public item: T,
    public distance?: number | null,
    public floorSimilarity?: number,
    public matches?: SearchResultMatches,
    public fuzzyScore?: number,
    public matchingWordsScore?: number,
    public allMatchLengthsScore?: number,
    public firstMatchLocationScore?: number,
    public weightedScore?: number,
    public normalizedProximity?: number,
    public type?: SearchResultType,
    public scoreWinner?: string,
  ) {}

  get itemId(): string | null {
    if (this.isPoi()) {
      return this.item.id.toString();
    } else if (this.isGeofence()) {
      return this.item.id.toString();
    } else if (this.isFlight()) {
      return this.item.flight.id;
    }
    return null;
  }

  isPoi(): this is SearchResultItem<Poi> {
    return this.type === SearchResultType.POI;
  }

  isGeofence(): this is SearchResultItem<Geofence> {
    return this.type === SearchResultType.GEOFENCE;
  }

  isFlight(): this is SearchResultItem<FlightWithPOI> {
    return this.type === SearchResultType.FLIGHT;
  }

  static fromMap(data: any) {
    if (data.type === SearchResultType.POI) {
      return new SearchResultItem(
        Poi.fromMap(data.item),
        data?.distance,
        data?.floorSimilarity,
        data?.matches,
        data?.fuzzyScore,
        data?.matchingWordsScore,
        data?.allMatchLengthsScore,
        data?.firstMatchLocationScore,
        data?.weightedScore,
        data?.normalizedProximity,
        data?.type,
        data?.scoreWinner,
      );
    } else if (data.type === SearchResultType.GEOFENCE) {
      return new SearchResultItem(
        Poi.fromMap(data.item),
        data?.distance,
        data?.floorSimilarity,
        data?.matches,
        data?.fuzzyScore,
        data?.matchingWordsScore,
        data?.allMatchLengthsScore,
        data?.firstMatchLocationScore,
        data?.weightedScore,
        data?.normalizedProximity,
        data?.type,
        data?.scoreWinner,
      );
    } else if (data.type === SearchResultType.FLIGHT) {
      const raw = data?.item;
      if (!raw || typeof raw !== "object") {
        return null;
      }
      const payload = {
        flight: raw.flight,
        poiId: raw.poiId ?? null,
      } as FlightWithPOI;
      return new SearchResultItem(
        payload,
        data?.distance,
        data?.floorSimilarity,
        data?.matches,
        data?.fuzzyScore,
        data?.matchingWordsScore,
        data?.allMatchLengthsScore,
        data?.firstMatchLocationScore,
        data?.weightedScore,
        data?.normalizedProximity,
        data?.type,
        data?.scoreWinner,
      );
    } else return null;
  }
}

export type SearchResultDict = { [id: string]: SearchResultItem };

export type SearchMessagePayload = {
  id: string;
  currentLocationLevel: number;
  selectedPoiCategories: PoiCategories;
  customPois: Poi[];
  distances: Record<string, number | null>;
  filterText: string | undefined;
  floorMap: Record<number, Floor>;
  fuzzyThreshold: number;
  lang: string;
  options: FilterOptions;
  poiCategories: PoiCategories;
  poiIdsToInclude: Poi["id"][];
  searchResultsBy: (typeof SearchResultsSearchBy)[number];
  sortResultsBy: (typeof SearchResultsSortBy)[number];
  flightCategoryId: number | null;
  searchTaskId: string;
};
export type PopulateMessagePayload = {
  type: SearchResultType;
  items: SearchableItem[];
};
export type InitMessagePayload = {
  type?: SearchResultType;
};
export type OrderMessagePayload = {
  distances: Record<string, number | null>;
  searchResults: SearchResultItem[];
  sortResultsBy: (typeof SearchResultsSortBy)[number];
};

export type SearchWorkerMessages =
  | {
      type: WORKER_EVENTS.INIT;
      payload: InitMessagePayload;
      messageId: string;
    }
  | {
      type: WORKER_EVENTS.POPULATE;
      payload: PopulateMessagePayload;
      messageId: string;
    }
  | {
      type: WORKER_EVENTS.SEARCH;
      payload: SearchMessagePayload;
      messageId: string;
    }
  | {
      type: WORKER_EVENTS.ORDER;
      payload: OrderMessagePayload;
      messageId: string;
    };
